import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import TagMultiSelect from '../../components/crm/TagMultiSelect';
import InfoTooltip from '../../components/ui/InfoTooltip';
import { fetchProspectFilterMeta, importProspects } from '../../api/prospects';
import { parseProspectCsv } from '../../utils/parseProspectCsv';
import { PROSPECT_IMPORT_FIELDS } from '../../utils/prospectImportFields';
import {
  buildApiMappingFromColumns,
  countRowsNotImportable,
  normalizeRequiredImportFields,
} from '../../utils/prospectImportRow';

const CSV_PREVIEW_MAX_ROWS = 200;
const STEPS = [
  { id: 1, label: 'Fichier et correspondance' },
  { id: 2, label: 'Confirmation' },
];

const REQUIRED_FIELD_TOOLTIP =
  'Plusieurs colonnes peuvent alimenter le même champ : leurs valeurs sont fusionnées (séparées par un espace). ' +
  'Si vous cochez « Ignorer si vide », la ligne n’est importée que lorsque ce résultat fusionné contient au moins une valeur. ' +
  'Une colonne peut rester vide si une autre colonne du même champ est renseignée.';

function ImportStepper({ current }) {
  return (
    <nav
      className="mb-8 flex flex-wrap items-center gap-2 text-sm"
      aria-label="Étapes d'import"
    >
      {STEPS.map((step, idx) => {
        const done = current > step.id;
        const active = current === step.id;
        return (
          <React.Fragment key={step.id}>
            {idx > 0 ? (
              <Icon name="ChevronRight" size={16} className="text-muted-foreground shrink-0" />
            ) : null}
            <span
              className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 font-medium ${
                active
                  ? 'bg-primary text-primary-foreground'
                  : done
                    ? 'bg-primary/15 text-primary'
                    : 'bg-muted text-muted-foreground'
              }`}
            >
              <span className="text-xs opacity-80">Étape {step.id}</span>
              {step.label}
            </span>
          </React.Fragment>
        );
      })}
    </nav>
  );
}

function CsvImportPreviewTable({
  headers,
  rows,
  columnToField,
  onSetMapping,
  requiredByField,
  onSetFieldRequired,
  primaryColumnByField,
}) {
  const previewRows = rows.slice(0, CSV_PREVIEW_MAX_ROWS);
  const truncated = rows.length > previewRows.length;

  return (
    <div className="overflow-auto rounded-lg border border-border bg-background shadow-sm max-h-[420px]">
      <table className="min-w-full border-collapse text-xs sm:text-sm">
        <thead className="sticky top-0 z-10 bg-muted">
          <tr>
            <th className="border border-border bg-muted px-2 py-2 text-left font-semibold text-foreground w-10 align-top">
              #
            </th>
            {headers.map((col) => {
              const field = columnToField[col] || '';
              const duplicateTarget =
                field && headers.filter((h) => columnToField[h] === field).length > 1;
              const isPrimaryForField = field && primaryColumnByField[field] === col;

              return (
                <th
                  key={col}
                  className="border border-border bg-muted px-2 py-2 text-left align-top min-w-[148px] max-w-[240px]"
                >
                  <div
                    className="font-semibold text-foreground truncate"
                    title={col}
                  >
                    {col}
                  </div>
                  <select
                    value={field}
                    onChange={(e) => onSetMapping(col, e.target.value)}
                    className="mt-1.5 w-full rounded-md border border-input bg-background px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {PROSPECT_IMPORT_FIELDS.map((opt) => (
                      <option key={opt.value || 'empty'} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {duplicateTarget ? (
                    <p className="mt-1 text-[10px] leading-snug text-primary">
                      Fusion avec «{' '}
                      {PROSPECT_IMPORT_FIELDS.find((f) => f.value === field)?.label} »
                    </p>
                  ) : null}
                  {!field ? null : isPrimaryForField ? (
                    <label className="mt-1.5 flex cursor-pointer items-start gap-1.5 text-[10px] leading-snug text-muted-foreground">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-input text-primary focus:ring-ring"
                        checked={!!requiredByField[field]}
                        onChange={(e) => onSetFieldRequired(field, e.target.checked)}
                      />
                      <span className="inline-flex items-center gap-0.5">
                        Ignorer si vide
                        <InfoTooltip text={REQUIRED_FIELD_TOOLTIP} />
                      </span>
                    </label>
                  ) : (
                    <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
                      Via {primaryColumnByField[field]}
                      {requiredByField[field] ? ' — ignorer si vide' : ''}
                    </p>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {previewRows.map((row, i) => (
            <tr key={i} className="even:bg-muted/30 hover:bg-primary/5">
              <td className="border border-border px-2 py-1.5 text-muted-foreground tabular-nums sticky left-0 z-[1] bg-inherit">
                {i + 1}
              </td>
              {headers.map((h) => (
                <td
                  key={h}
                  className="border border-border px-2 py-1.5 text-foreground max-w-[240px] truncate"
                  title={row[h] || ''}
                >
                  {row[h] || '—'}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {truncated ? (
        <p className="border-t border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Aperçu limité aux {CSV_PREVIEW_MAX_ROWS} premières lignes — l&apos;import inclura les{' '}
          {rows.length} lignes du fichier.
        </p>
      ) : null}
    </div>
  );
}

export default function ProspectImportPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [columnToField, setColumnToField] = useState({});
  const [requiredByField, setRequiredByField] = useState({});
  const [tagsForAll, setTagsForAll] = useState([]);
  const [selectedCampaignId, setSelectedCampaignId] = useState('');
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState(null);

  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingCampaigns(true);
      try {
        const res = await fetchProspectFilterMeta();
        if (!cancelled) setCampaignOptions(res?.data?.campaigns ?? []);
      } catch (e) {
        console.error(e);
        if (!cancelled) setCampaignOptions([]);
      } finally {
        if (!cancelled) setLoadingCampaigns(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCampaign = useMemo(
    () => campaignOptions.find((c) => c.id === selectedCampaignId) || null,
    [campaignOptions, selectedCampaignId],
  );

  const importTags = useMemo(() => {
    const fromCampaign = Array.isArray(selectedCampaign?.tags) ? selectedCampaign.tags : [];
    return Array.from(
      new Set(
        [...fromCampaign, ...tagsForAll].map((t) => String(t).trim()).filter(Boolean),
      ),
    );
  }, [selectedCampaign, tagsForAll]);

  const apiMapping = useMemo(
    () => buildApiMappingFromColumns(columnToField),
    [columnToField],
  );

  const mappedFieldCount = useMemo(
    () => Object.values(columnToField).filter(Boolean).length,
    [columnToField],
  );

  const primaryColumnByField = useMemo(() => {
    const map = {};
    headers.forEach((col) => {
      const f = columnToField[col];
      if (f && map[f] == null) map[f] = col;
    });
    return map;
  }, [headers, columnToField]);

  const requiredFieldsForImport = useMemo(
    () =>
      normalizeRequiredImportFields(
        Object.entries(requiredByField)
          .filter(([, on]) => on)
          .map(([f]) => f),
        apiMapping,
      ),
    [requiredByField, apiMapping],
  );

  const rowsNotImportable = useMemo(
    () => countRowsNotImportable(rows, apiMapping, importTags, requiredFieldsForImport),
    [rows, apiMapping, importTags, requiredFieldsForImport],
  );

  const rowsImportable = Math.max(0, rows.length - rowsNotImportable);

  const handleFile = (fileList) => {
    const f = fileList?.[0];
    if (
      !f ||
      (!f.name.toLowerCase().endsWith('.csv') &&
        !f.type.includes('csv') &&
        !f.type.includes('sheet'))
    ) {
      setFileError('Veuillez sélectionner un fichier CSV.');
      return;
    }
    setFileError(null);
    setImportResult(null);
    setImportError(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const { headers: h, rows: r } = parseProspectCsv(e.target.result || '');
        if (!h.length || !r.length) {
          setFileError('Le fichier ne contient pas de colonnes ou de lignes de données.');
          return;
        }
        setFile(f);
        setHeaders(h);
        setRows(r);
        setColumnToField({});
        setRequiredByField({});
        setStep(1);
      } catch {
        setFileError('Impossible de lire le CSV.');
      }
    };
    reader.readAsText(f, 'utf-8');
  };

  const setMapping = (column, field) => {
    setColumnToField((prev) => {
      const next = { ...prev };
      if (field) next[column] = field;
      else delete next[column];
      return next;
    });
  };

  const setFieldRequired = (field, required) => {
    if (!field) return;
    setRequiredByField((prev) => {
      const next = { ...prev };
      if (required) next[field] = true;
      else delete next[field];
      return next;
    });
  };

  const handleImport = async () => {
    if (!rows.length || importing) return;
    setImporting(true);
    setImportError(null);
    setImportResult(null);
    try {
      const res = await importProspects(
        rows,
        apiMapping,
        importTags,
        requiredFieldsForImport,
      );
      const data = res?.data ?? {};
      setImportResult({
        created: data.created ?? 0,
        updated: data.updated ?? 0,
        total: data.total ?? rows.length,
        skipped:
          typeof data.skipped === 'number'
            ? data.skipped
            : (data.errors ?? []).length,
      });
    } catch (e) {
      setImportError(e?.message || e?.data?.message || "Erreur lors de l'import.");
    } finally {
      setImporting(false);
    }
  };

  const resetFile = () => {
    setStep(1);
    setFile(null);
    setHeaders([]);
    setRows([]);
    setColumnToField({});
    setRequiredByField({});
    setTagsForAll([]);
    setSelectedCampaignId('');
    setFileError(null);
    setImportError(null);
    setImportResult(null);
  };

  const importedTotal =
    importResult != null ? (importResult.created ?? 0) + (importResult.updated ?? 0) : 0;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Import prospects - CRM</title>
      </Helmet>
      <Header />
      <Breadcrumb />
      <main className="main-content max-w-6xl mx-auto pb-12">
        <Button
          variant="ghost"
          size="sm"
          iconName="ArrowLeft"
          iconPosition="left"
          className="mb-4"
          onClick={() => navigate('/prospect-contact-directory')}
        >
          Annuaire
        </Button>

        <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-1">
          Import de prospects
        </h1>
        <p className="text-sm text-muted-foreground mb-6">
          Import en lot depuis un fichier CSV — {rows.length > 0 ? `${rows.length} ligne(s)` : 'étape par étape'}.
        </p>

        <ImportStepper current={step} />

        {step === 1 ? (
          <section className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-2">1. Fichier et correspondance</h2>
              <p className="text-sm text-muted-foreground mb-4">
                Formats acceptés : CSV (virgule ou point-virgule). Sous chaque en-tête de colonne,
                choisissez le champ prospect correspondant. Plusieurs colonnes peuvent alimenter le
                même champ (fusion avec un espace). Campagne et tags à l&apos;étape suivante.
              </p>
              <div
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-smooth ${
                  dragOver ? 'border-primary bg-primary/5' : 'border-border bg-card'
                }`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  handleFile(e.dataTransfer?.files ? [e.dataTransfer.files[0]] : null);
                }}
              >
                <Icon name="Upload" size={40} className="mx-auto mb-3 text-muted-foreground" />
                <p className="text-sm font-medium text-foreground mb-1">Déposez un fichier CSV ici</p>
                <p className="text-xs text-muted-foreground mb-4">ou</p>
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-muted px-4 py-2 text-sm font-medium hover:bg-muted/80">
                  <span>Parcourir</span>
                  <input
                    type="file"
                    accept=".csv,text/csv,application/csv"
                    className="sr-only"
                    onChange={(e) => handleFile(e.target.files ? [e.target.files[0]] : null)}
                  />
                </label>
                {file ? (
                  <p className="mt-4 text-xs text-muted-foreground">
                    Fichier : <span className="font-medium text-foreground">{file.name}</span>
                  </p>
                ) : null}
              </div>
              {fileError ? <p className="mt-3 text-sm text-destructive">{fileError}</p> : null}
            </div>

            {headers.length > 0 ? (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">
                  Aperçu et mapping
                  <span className="ml-2 font-normal text-muted-foreground">
                    ({Math.min(CSV_PREVIEW_MAX_ROWS, rows.length)} / {rows.length} lignes — faites
                    défiler le tableau)
                  </span>
                </h3>
                <CsvImportPreviewTable
                  headers={headers}
                  rows={rows}
                  columnToField={columnToField}
                  onSetMapping={setMapping}
                  requiredByField={requiredByField}
                  onSetFieldRequired={setFieldRequired}
                  primaryColumnByField={primaryColumnByField}
                />
              </div>
            ) : null}

            {mappedFieldCount === 0 && headers.length > 0 ? (
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Associez au moins une colonne pour continuer.
              </p>
            ) : null}

            <div className="flex flex-wrap justify-between gap-2 pt-2">
              {headers.length > 0 ? (
                <Button type="button" variant="outline" onClick={resetFile}>
                  Changer de fichier
                </Button>
              ) : (
                <span />
              )}
              <Button
                type="button"
                disabled={!rows.length || mappedFieldCount === 0}
                onClick={() => setStep(2)}
                iconName="ArrowRight"
                iconPosition="right"
              >
                Continuer — confirmation
              </Button>
            </div>
          </section>
        ) : null}

        {step === 2 ? (
          <section className="space-y-8">
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-2">
                2. Campagne, tags et import
              </h2>
              <p className="text-sm text-muted-foreground">
                Choisissez la campagne et les tags à appliquer, vérifiez le résumé, puis lancez
                l&apos;import. Sur{' '}
                <span className="font-medium text-foreground">{rows.length}</span> ligne(s),{' '}
                <span className="font-medium text-foreground">{rowsImportable}</span> ligne(s)
                importable(s)
                {requiredFieldsForImport.length > 0 ? (
                  <>
                    {' '}
                    ({requiredFieldsForImport.length} champ(s) avec valeur exigée)
                  </>
                ) : null}
                {rowsNotImportable > 0 ? (
                  <>
                    {' '}
                    —{' '}
                    <span className="text-amber-700 dark:text-amber-400">
                      {rowsNotImportable} ignorée(s)
                    </span>
                  </>
                ) : null}
                .
              </p>
            </div>

            <div className="rounded-xl border border-border bg-card p-4 sm:p-5 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  Campagne (optionnel)
                </label>
                <p className="mb-2 text-xs text-muted-foreground">
                  Les tags de la campagne sont appliqués à tous les prospects importés (en plus de
                  ceux du CSV et des tags ci-dessous).
                </p>
                <select
                  value={selectedCampaignId}
                  onChange={(e) => setSelectedCampaignId(e.target.value)}
                  disabled={loadingCampaigns}
                  className="w-full max-w-md rounded-md border border-input bg-background px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">
                    {loadingCampaigns ? 'Chargement…' : 'Aucune campagne'}
                  </option>
                  {campaignOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                      {Array.isArray(c.tags) && c.tags.length > 0
                        ? ` — ${c.tags.join(', ')}`
                        : ' — sans tag'}
                    </option>
                  ))}
                </select>
              </div>
              <TagMultiSelect
                label="Tags additionnels"
                value={tagsForAll}
                onChange={setTagsForAll}
                placeholder="Autres tags à appliquer à tout le fichier…"
              />
              {importTags.length > 0 ? (
                <p className="text-xs text-muted-foreground">
                  Tags appliqués à chaque ligne :{' '}
                  <span className="font-medium text-foreground">{importTags.join(', ')}</span>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  Aucun tag global — seuls les tags mappés depuis le CSV seront utilisés.
                </p>
              )}
            </div>

            {!importResult && !importing ? (
              <div className="flex flex-wrap justify-between gap-2">
                <Button type="button" variant="outline" onClick={() => setStep(1)}>
                  Retour au fichier et mapping
                </Button>
                <Button
                  type="button"
                  iconName="Upload"
                  iconPosition="left"
                  disabled={!rows.length || mappedFieldCount === 0}
                  onClick={handleImport}
                >
                  Lancer l&apos;import
                </Button>
              </div>
            ) : null}

            {importing ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="mb-6 h-2 w-full max-w-md overflow-hidden rounded-full bg-muted">
                  <div className="h-full w-1/3 animate-pulse rounded-full bg-primary" />
                </div>
                <p className="text-lg font-medium text-foreground">Import en cours…</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  Traitement de {rows.length} ligne(s), merci de patienter.
                </p>
              </div>
            ) : null}

            {importError ? (
              <p className="text-center text-sm text-destructive">{importError}</p>
            ) : null}

            {importResult && !importing ? (
              <div className="flex flex-col items-center justify-center py-12 text-center space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 w-full max-w-lg">
                  <div className="rounded-2xl border border-success/30 bg-success/5 px-6 py-8">
                    <p className="text-4xl font-bold tabular-nums text-success">{importedTotal}</p>
                    <p className="mt-2 text-sm font-medium text-foreground">Prospects importés</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {importResult.created} créé(s) · {importResult.updated} complété(s)
                    </p>
                  </div>
                  <div className="rounded-2xl border border-border bg-muted/30 px-6 py-8">
                    <p className="text-4xl font-bold tabular-nums text-foreground">
                      {importResult.skipped}
                    </p>
                    <p className="mt-2 text-sm font-medium text-foreground">Lignes non importées</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Valeur fusionnée manquante ou erreur
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  <Button type="button" variant="outline" onClick={resetFile}>
                    Nouvel import
                  </Button>
                  <Link to="/prospect-contact-directory">
                    <Button iconName="Users" iconPosition="left">
                      Voir l&apos;annuaire
                    </Button>
                  </Link>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}
      </main>
    </div>
  );
}
